import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// import ForgotPasswordBanner from '../../../assets/images/login/forgot-password.png';
import { currentBranding } from '../../../config/branding';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneNumber from '../../../shared-components/custom-phone-number-input/CustomPhoneNumberInput';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import { setLocalStorageItem } from '../../../shared-functions/LocalStorage';
import { emailValidation, phoneNumberValidation } from '../../../shared-functions/Validation';
import auth from '../../../utils/api/auth';
// import OtpVerifyBanner from '../../login/components/OtpVerifyBanner';

/**
 * Forget password component
 * @returns {React.JSX.Element} The rendered Forget password component.
 * @description Forget password component is a component for forget password functionality.
 */
function ForgetPassword() {
  const { t, i18n } = useTranslation('loginV2');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [credentials, setCredentials] = useState<{
    accountCustomId: string;
    emailOrPhoneNumber: string;
    countryCode: string;
    type: string | number;
  }>({
    accountCustomId: '',
    emailOrPhoneNumber: '',
    type: 'email',
    countryCode: currentBranding === 'energic' ? '966' : '1',
  });

  const [formStatus, setFormStatus] = useState({
    showFormErrors: false,
    errorMessage: '',
    showToastMessage: false,
    buttonLoader: false,
  });

  /**
   * Get input change value and store in credentials state
   * @param name
   * @param changeValue
   */
  const handleCredentialChange = (name: string, changeValue: string | number) => {
    if (name === 'type') {
      setCredentials({
        ...credentials,
        [name]: changeValue,
        emailOrPhoneNumber: '',
      });
    } else {
      setCredentials({
        ...credentials,
        [name]: changeValue,
      });
    }
  };

  /**
   * Function to submit entered cre
   */
  const handleSubmit = () => {
    setFormStatus({
      showFormErrors: true,
      errorMessage: '',
      showToastMessage: false,
      buttonLoader: false,
    });
    if (
      credentials.type !== '' &&
      credentials.countryCode !== '' &&
      (phoneNumberValidation(credentials.emailOrPhoneNumber) ||
        emailValidation(credentials.emailOrPhoneNumber))
    ) {
      setFormStatus({
        showFormErrors: false,
        errorMessage: '',
        showToastMessage: false,
        buttonLoader: true,
      });
      const request: any = {
        countryCode: credentials.countryCode,
        emailOrPhoneNumber: credentials.emailOrPhoneNumber,
      };

      if (credentials?.accountCustomId) {
        request.accountCustomId = credentials.accountCustomId;
      }

      auth
        .forgotPassword(request)
        .then(() => {
          setFormStatus({
            showFormErrors: false,
            errorMessage: '',
            showToastMessage: false,
            buttonLoader: false,
          });
          setLocalStorageItem('passwordReset', credentials);
          navigate('../login/forgot-password-otp-verification');
        })
        .catch((error) => {
          setFormStatus({
            showFormErrors: true,
            errorMessage: error?.response?.data?.message || '',
            showToastMessage: true,
            buttonLoader: false,
          });
          // Redirect to OTP enter page even if the OTP not send
          // setLocalStorageItem('passwordReset', credentials);
          // navigate('../login/forgot-password-otp-verification');

          // Display a user-friendly message
          dispatch(
            setNotificationDetails({
              type: 'error',
              notificationDetails: {
                statusCode: error?.response?.data?.code || 200,
                message: error?.response?.data?.message || 'Something went wrong!',
              },
            }),
          );
        });
    }
  };

  return (
    <div className="forgot-password-v2-wrap" dir={i18n.dir()}>
      <div className="forgot-password-v2-container">
        <div className="email-phone-toggle-wrap">
          <RadioButton
            name="type"
            value={credentials.type}
            list={[
              {
                label: `${t('email')}`,
                value: 'email',
              },
              {
                label: `${t('phoneNumber')}`,
                value: 'phone',
              },
            ]}
            onChangeValue={handleCredentialChange}
          />
          {credentials.type === 'email' ? (
            <CustomInput
              type="text"
              placeholder={t('enterEmail')}
              name="emailOrPhoneNumber"
              valueRequired
              value={credentials.emailOrPhoneNumber}
              onChangeValue={handleCredentialChange}
              error={!emailValidation(credentials.emailOrPhoneNumber) && formStatus.showFormErrors}
              errorMessage={t('invalidEmailId')}
            />
          ) : (
            <CustomPhoneNumber
              placeholder={t('enterPhoneNumber')}
              name="emailOrPhoneNumber"
              value={credentials.emailOrPhoneNumber}
              onChangeValue={handleCredentialChange}
              valueRequired
              error={
                !phoneNumberValidation(credentials.emailOrPhoneNumber) && formStatus.showFormErrors
              }
              errorMessage={t('invalidPhoneNumber')}
              countryCode={credentials.countryCode}
            />
          )}
        </div>
        <CustomInput
          inputLabel={t('inputLabelAccountId')}
          type="text"
          placeholder={t('enterAccountId')}
          name="accountCustomId"
          value={credentials.accountCustomId}
          onChangeValue={handleCredentialChange}
        />
        <MyButton
          label={t('getOtp')}
          buttonType="submit"
          onClickFunc={handleSubmit}
          loading={formStatus.buttonLoader}
          disableButton={formStatus.buttonLoader}
        />
      </div>
    </div>
  );
}

export default ForgetPassword;
